import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ApiService } from 'app/shared/api.service';
import Swal from 'sweetalert2';

interface OrderProduct {
    id: string;
    quantity: number;
}

interface OrderPayload {
    name: string;
    phone: string;
    email: string | null;
    address: string;
    location_id: string | null;
    properties: any[];
    products: OrderProduct[];
    voucher_id: string | null;
}

interface Order {
    id: string;
    name: string;
    phone: string;
    email: string | null;
    address: string;
    location_id: string | null;
    properties: any[];
    products: any[];
    voucher_id: string | null;
    created_at?: string;
    updated_at?: string;
}

interface Product {
    id: string;
    name: string;
    price: number;
    sku: string;
}

interface Pagination {
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
}

@Component({
    selector: 'app-order',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './order.component.html',
    styleUrl: './order.component.scss'
})
export class OrderComponent {
    isModalOpen = false;
    isEditMode = false;
    isSubmitting = false;

    Order: Order[] = [];
    Products: Product[] = [];
    pagination: Pagination = {
        current_page: 1,
        per_page: 100,
        total: 0,
        last_page: 1
    };

    orderForm: OrderPayload = {
        name: '',
        phone: '',
        email: null,
        address: '',
        location_id: null,
        properties: [],
        products: [],
        voucher_id: null
    };

    selectedOrderId: string = '';

    constructor(
        private apiService: ApiService,
        private router: Router
    ) {}

    ngOnInit(): void {
        this.getOrders();
        this.getProducts();
    }

    // ==================== CRUD OPERATIONS ====================

    // GET - List Orders
    async getOrders(): Promise<void> {
        try {
            const response = await this.apiService.get(
                `/api/v1/tenant/manage/order/list?page=${this.pagination.current_page}&per_page=${this.pagination.per_page}`
            );
            // console.log(response);
            this.Order = response.data.list;
            this.pagination = response.data.pagination;
        } catch (error) {
            // console.error('Error fetching orders:', error);
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Terjadi kesalahan saat memuat data order',
                confirmButtonColor: '#dc3545',
                timer: 2000,
                showConfirmButton: false,
            });
        }
    }

    // GET - List Products for selection
    async getProducts(): Promise<void> {
        try {
            const response = await this.apiService.get(
                '/api/v1/tenant/manage/product/list?page=1&per_page=1000'
            );
            this.Products = response.data.list;
        } catch (error) {
            // console.error('Error fetching products:', error);
        }
    }

    // GET - Single Order (untuk edit)
    async getOrderById(orderId: string): Promise<void> {
        try {
            const response = await this.apiService.get(
                `/api/v1/tenant/manage/order/${orderId}`
            );

            // Populate form dengan data order
            this.orderForm = {
                name: response.data.name,
                phone: response.data.phone,
                email: response.data.email,
                address: response.data.address,
                location_id: response.data.location_id,
                properties: response.data.properties || [],
                products: response.data.products.map((p: any) => ({
                    id: p.id,
                    quantity: p.quantity || 1
                })),
                voucher_id: response.data.voucher_id
            };

            this.selectedOrderId = orderId;
            this.isEditMode = true;
            this.isModalOpen = true;
        } catch (error) {
            // console.error('Error fetching order:', error);
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Gagal memuat data order',
                confirmButtonColor: '#dc3545',
                timer: 2000,
                showConfirmButton: false,
            });
        }
    }

    // POST - Create Order
    async createOrder(): Promise<void> {
        if (!this.validateOrderForm()) {
            return;
        }

        this.isSubmitting = true;

        try {
            await this.apiService.post('/api/v1/tenant/manage/order', this.orderForm);

            await Swal.fire({
                icon: 'success',
                title: 'Berhasil',
                text: 'Order berhasil ditambahkan',
                confirmButtonColor: '#28a745',
                timer: 2000,
                showConfirmButton: false,
            });

            this.closeModal();
            this.getOrders();
        } catch (error: any) {
            // console.error('Error creating order:', error);
            await Swal.fire({
                icon: 'error',
                title: 'Gagal',
                text: error?.error?.message || 'Gagal menambahkan order',
                confirmButtonColor: '#dc3545',
            });
        } finally {
            this.isSubmitting = false;
        }
    }

    // PUT - Update Order
    async updateOrder(): Promise<void> {
        if (!this.validateOrderForm()) {
            return;
        }

        this.isSubmitting = true;

        try {
            await this.apiService.put(
                `/api/v1/tenant/manage/order/${this.selectedOrderId}`,
                this.orderForm
            );

            await Swal.fire({
                icon: 'success',
                title: 'Berhasil',
                text: 'Order berhasil diupdate',
                confirmButtonColor: '#28a745',
                timer: 2000,
                showConfirmButton: false,
            });

            this.closeModal();
            this.getOrders();
        } catch (error: any) {
            // console.error('Error updating order:', error);
            await Swal.fire({
                icon: 'error',
                title: 'Gagal',
                text: error?.error?.message || 'Gagal mengupdate order',
                confirmButtonColor: '#dc3545',
            });
        } finally {
            this.isSubmitting = false;
        }
    }

    // DELETE - Delete Order
    async deleteOrder(orderId: string, customerName: string): Promise<void> {
        const result = await Swal.fire({
            title: 'Konfirmasi Hapus',
            text: `Apakah Anda yakin ingin menghapus order dari "${customerName}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Ya, Hapus',
            cancelButtonText: 'Batal',
        });

        if (result.isConfirmed) {
            try {
                await this.apiService.delete('/api/v1/tenant/manage/order', orderId);

                await Swal.fire({
                    icon: 'success',
                    title: 'Berhasil',
                    text: 'Order berhasil dihapus',
                    confirmButtonColor: '#28a745',
                    timer: 2000,
                    showConfirmButton: false,
                });

                this.getOrders();
            } catch (error: any) {
                // console.error('Error deleting order:', error);
                await Swal.fire({
                    icon: 'error',
                    title: 'Gagal',
                    text: error?.error?.message || 'Gagal menghapus order',
                    confirmButtonColor: '#dc3545',
                });
            }
        }
    }

    // ==================== MODAL OPERATIONS ====================

    openAddModal(): void {
        this.isEditMode = false;
        this.resetForm();
        this.isModalOpen = true;
    }

    openEditModal(orderId: string): void {
        this.getOrderById(orderId);
    }

    closeModal(): void {
        this.isModalOpen = false;
        this.resetForm();
    }

    resetForm(): void {
        this.orderForm = {
            name: '',
            phone: '',
            email: null,
            address: '',
            location_id: null,
            properties: [],
            products: [{ id: '', quantity: 1 }],
            voucher_id: null
        };
        this.selectedOrderId = '';
    }

    // ==================== PRODUCT MANAGEMENT ====================

    addProductRow(): void {
        this.orderForm.products.push({ id: '', quantity: 1 });
    }

    removeProductRow(index: number): void {
        if (this.orderForm.products.length > 1) {
            this.orderForm.products.splice(index, 1);
        } else {
            Swal.fire({
                icon: 'warning',
                title: 'Peringatan',
                text: 'Order harus memiliki minimal 1 produk',
                confirmButtonColor: '#ffc107',
            });
        }
    }

    getProductName(productId: string): string {
        const product = this.Products.find(p => p.id === productId);
        return product ? product.name : '';
    }

    getProductPrice(productId: string): number {
        const product = this.Products.find(p => p.id === productId);
        return product ? product.price : 0;
    }

    calculateSubtotal(productId: string, quantity: number): number {
        return this.getProductPrice(productId) * quantity;
    }

    calculateTotal(): number {
        return this.orderForm.products.reduce((total, item) => {
            return total + this.calculateSubtotal(item.id, item.quantity);
        }, 0);
    }

    // ==================== FORM VALIDATION ====================

    validateOrderForm(): boolean {
        // Validate required fields
        if (!this.orderForm.name || !this.orderForm.phone || !this.orderForm.address) {
            Swal.fire({
                icon: 'warning',
                title: 'Validasi Gagal',
                text: 'Name, Phone, dan Address wajib diisi',
                confirmButtonColor: '#ffc107',
            });
            return false;
        }

        // Validate phone format
        const phoneRegex = /^08\d{8,11}$/;
        if (!phoneRegex.test(this.orderForm.phone)) {
            Swal.fire({
                icon: 'warning',
                title: 'Validasi Gagal',
                text: 'Phone harus diawali 08 dan maksimal 13 karakter',
                confirmButtonColor: '#ffc107',
            });
            return false;
        }

        // Validate products
        if (this.orderForm.products.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Validasi Gagal',
                text: 'Order harus memiliki minimal 1 produk',
                confirmButtonColor: '#ffc107',
            });
            return false;
        }

        // Validate each product has ID and quantity
        for (let i = 0; i < this.orderForm.products.length; i++) {
            const product = this.orderForm.products[i];
            if (!product.id || product.quantity < 1) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Validasi Gagal',
                    text: `Produk #${i + 1}: Pilih produk dan masukkan quantity minimal 1`,
                    confirmButtonColor: '#ffc107',
                });
                return false;
            }
        }

        return true;
    }

    // ==================== PAGINATION ====================

    async goToPage(page: number): Promise<void> {
        if (page < 1 || page > this.pagination.last_page) {
            return;
        }
        this.pagination.current_page = page;
        await this.getOrders();
    }

    async nextPage(): Promise<void> {
        await this.goToPage(this.pagination.current_page + 1);
    }

    async previousPage(): Promise<void> {
        await this.goToPage(this.pagination.current_page - 1);
    }

    // ==================== HANDLER SUBMIT ====================

    async onSubmit(): Promise<void> {
        if (this.isEditMode) {
            await this.updateOrder();
        } else {
            await this.createOrder();
        }
    }

    // ==================== HELPER METHODS ====================

    getTotalProducts(order: Order): number {
        return order.products?.length || 0;
    }
}
